import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { FontProvider } from './contexts/FontContext';
import { TicketProvider } from './contexts/TicketContext';
import { ProjectProvider } from './contexts/ProjectContext';
import { NotificationProvider } from './contexts/NotificationContext';
import MainLayout from './layouts/MainLayout';
import Loading from './components/ui/Loading';
import LoginPage from './pages/LoginPage';
import ChangePasswordPage from './pages/ChangePasswordPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import DashboardPage from './pages/DashboardPage';
import BoardPage from './pages/BoardPage';
import TicketPage from './pages/TicketPage';
import ProjectsPage from './pages/ProjectsPage';
import CreateProjectPage from './pages/CreateProjectPage';
import ProjectStatusManagement from './pages/ProjectStatusManagement';
import ReportsPage from './pages/ReportsPage';
import SettingsPage from './pages/SettingsPage';
import ProfilePage from './pages/ProfilePage';
import ModuleSelectionPage from './pages/ModuleSelectionPage';
import ChangelogPage from './pages/ChangelogPage';
import StopSaleDashboard from './pages/stopsale/StopSaleDashboard';
import StopSaleActions from './pages/stopsale/StopSaleActions';
import StopSaleCalendar from './pages/stopsale/StopSaleCalendar';
import StopSaleMailRecipientsPage from './pages/stopsale/StopSaleMailRecipientsPage';
import MarketingBudgetDashboard from './pages/marketingbudget/MarketingBudgetDashboard';
import MarketingBudget from './pages/marketingbudget/MarketingBudget';
import BasicBudget from './pages/marketingbudget/BasicBudget';
import PercentBudget from './pages/marketingbudget/PercentBudget';
import MarketingBudgetReports from './pages/marketingbudget/MarketingBudgetReports';
import MarketingBudgetTables from './pages/marketingbudget/MarketingBudgetTables';
import MarketingBudgetDefinitions from './pages/marketingbudget/MarketingBudgetDefinitions';
import MarketingBudgetAnalysis from './pages/marketingbudget/MarketingBudgetAnalysis';
import ManagerViewPage from './pages/marketingbudget/ManagerViewPage';
import AirportTouristData from './pages/marketingbudget/AirportTouristData';
import ContentManagement from './pages/contentmanagement/ContentManagement';
import SalesCallPage from './pages/salescall/SalesCallPage';
import TestBudgetGrid from './pages/marketingbudget/TestBudgetGrid';
import RoadPlannerMonthly from './pages/salescall/RoadPlannerMonthly';
import TravelExpensesPage from './pages/salescall/TravelExpensesPage';
import SalesCallMailSchedulesPage from './pages/salescall/SalesCallMailSchedulesPage';
import B2BMeetingsDashboard from './pages/salescall/B2BMeetingsDashboard';
import ComparativeVisitReport from './pages/salescall/ComparativeVisitReport';
import AIInsightsDashboard from './pages/salesandmarketing/AIInsightsDashboard';
import AIRecordAnalyses from './pages/salesandmarketing/AIRecordAnalyses';
import SurveyDashboard from './pages/survey/SurveyDashboard';
import SurveyList from './pages/survey/SurveyList';
import SurveyCreate from './pages/survey/SurveyCreate';
import SurveyFill from './pages/survey/SurveyFill';
import SurveySubmissions from './pages/survey/SurveySubmissions';
import SurveySubmissionDetail from './pages/survey/SurveySubmissionDetail';
import SurveyAnalytics from './pages/survey/SurveyAnalytics';
import SurveyGlobalAnalytics from './pages/survey/SurveyGlobalAnalytics';
import SurveyGlobalSubmissions from './pages/survey/SurveyGlobalSubmissions';
import AddressBookPage from './pages/salescall/AddressBookPage';
import SubAgencyPage from './pages/salescall/SubAgencyPage';
import MarketingManagementPage from './pages/mailingmanagement/MarketingManagementPage';
import MailingPlanner from './pages/mailingmanagement/MailingPlanner';
import MailingCalendar from './pages/mailingmanagement/MailingCalendar';
import ContentPlanner from './pages/mailingmanagement/ContentPlanner';
import ContentCalendar from './pages/mailingmanagement/ContentCalendar';
import ContentPresentationTable from './pages/mailingmanagement/ContentPresentationTable';
import NotesLayout from './pages/notes/NotesLayout';
import NotesPage from './pages/notes/NotesPage';
import QualityManagementPage from './pages/qualitymanagement/QualityManagementPage';
import QualityDofAccessManagementPage from './pages/qualitymanagement/QualityDofAccessManagementPage';
import DoifDashboardPage from './pages/qualitymanagement/DoifDashboardPage';
import FoodBeveragePage from './pages/foodbeverage/FoodBeveragePage';
import LicenseDashboard from './pages/general/LicenseDashboard';
import LicenseListPage from './pages/general/LicenseListPage';
import LicenseReportsPage from './pages/general/LicenseReportsPage';
import LicenseDefinitionsPage from './pages/general/LicenseDefinitionsPage';
import QrCodePage from './pages/general/QrCodePage';
import CustomPagesPage from './pages/general/CustomPagesPage';
import InternalAuditVehiclesPage from './pages/internalaudit/InternalAuditVehiclesPage';
import InternalAuditInspectionsPage from './pages/internalaudit/InternalAuditInspectionsPage';
import InternalAuditInsurancePage from './pages/internalaudit/InternalAuditInsurancePage';
import InternalAuditMaintenancePage from './pages/internalaudit/InternalAuditMaintenancePage';
import InternalAuditMileagePage from './pages/internalaudit/InternalAuditMileagePage';
import InternalAuditTrafficFinesPage from './pages/internalaudit/InternalAuditTrafficFinesPage';
import InternalAuditBreakdownsPage from './pages/internalaudit/InternalAuditBreakdownsPage';
import InternalAuditAccidentsPage from './pages/internalaudit/InternalAuditAccidentsPage';
import InternalAuditAccessManagementPage from './pages/internalaudit/InternalAuditAccessManagementPage';
import InternalAuditVehicleAuditLogPage from './pages/internalaudit/InternalAuditVehicleAuditLogPage';
import VehicleMaintenanceAccessPage from './pages/vehiclemaintenance/VehicleMaintenanceAccessPage';
import ErrorBoundary from './components/ErrorBoundary';
import MicrosoftMailLoginPage from './pages/MicrosoftMailLoginPage';
import MailCallCallbackPage from './pages/MailCallCallbackPage';
import MailModuleLayout from './pages/microsoft-mail/MailModuleLayout';
import WordToExcelPage from './pages/docconvert/WordToExcelPage';
import DailyNotesPage from './pages/docconvert/DailyNotesPage';
import NotesDashboardPage from './pages/docconvert/NotesDashboardPage';
import MyNotesDashboardPage from './pages/docconvert/MyNotesDashboardPage';
import NoteFoldersPage from './pages/docconvert/NoteFoldersPage';
import DocConvertAccessPage from './pages/docconvert/DocConvertAccessPage';
import TranscriptionPage from './pages/transcription/TranscriptionPage';
import TranscriptionAccessPage from './pages/transcription/TranscriptionAccessPage';
import TechnicalMaintenanceDashboardPage from './pages/technicalmaintenance/TechnicalMaintenanceDashboardPage';
import TechnicalMaintenanceEquipmentPage from './pages/technicalmaintenance/TechnicalMaintenanceEquipmentPage';
import TechnicalMaintenanceRecordsPage from './pages/technicalmaintenance/TechnicalMaintenanceRecordsPage';
import TechnicalMaintenanceInvoicesPage from './pages/technicalmaintenance/TechnicalMaintenanceInvoicesPage';
import TechnicalMaintenanceAccessPage from './pages/technicalmaintenance/TechnicalMaintenanceAccessPage';
import TechnicalMaintenanceActivityPage from './pages/technicalmaintenance/TechnicalMaintenanceActivityPage';
import TechnicalMaintenanceSchedulePage from './pages/technicalmaintenance/TechnicalMaintenanceSchedulePage';
import TechnicalMaintenanceEquipmentDetailPage from './pages/technicalmaintenance/TechnicalMaintenanceEquipmentDetailPage';
import TechnicalMaintenanceReportsPage from './pages/technicalmaintenance/TechnicalMaintenanceReportsPage';
import CamasirhaneLayout from './pages/camasirhane/CamasirhaneLayout';
import CamasirhaneAccessPage from './pages/camasirhane/CamasirhaneAccessPage';
import WeighingEntryScreen from './pages/camasirhane/components/WeighingEntryScreen';
import ProductionEntryScreen from './pages/camasirhane/components/ProductionEntryScreen';
import SummaryTotalsScreen from './pages/camasirhane/components/SummaryTotalsScreen';
import CostEntryScreen from './pages/camasirhane/components/CostEntryScreen';
import IronMeasurementScreen from './pages/camasirhane/components/IronMeasurementScreen';
import ImportExportScreen from './pages/camasirhane/components/ImportExportScreen';
import AdminScreen from './pages/camasirhane/components/AdminScreen';
import DefinitionsScreen from './pages/camasirhane/components/DefinitionsScreen';
import DashboardScreen from './pages/camasirhane/components/DashboardScreen';

// Protected Route component'i
const ProtectedRoute = ({ children, adminOnly = false, workReportAccess = false }) => {
  const { isAuthenticated, loading, user, isAdmin, canAccessWorkReport } = useAuth();
  const location = useLocation();

  if (loading) {
    return <Loading fullScreen={true} message="Kimlik doğrulanıyor..." />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Eğer kullanıcı şifre değiştirmek zorundaysa VE zaten şifre değiştirme sayfasında DEĞİLSE, yönlendir.
  if (user?.must_change_password && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }

  // Eğer kullanıcı şifresini değiştirdiyse VE şifre değiştirme sayfasındaysa, proje seçimine yönlendir.
  if (!user?.must_change_password && location.pathname === '/change-password') {
    return <Navigate to="/module-selection" replace />;
  }

  if (adminOnly && !isAdmin()) {
    return <Navigate to="/dashboard" replace />;
  }

  if (workReportAccess && !canAccessWorkReport()) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Public Route component'i (login sayfası için)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <Loading fullScreen={true} message="Sistem Hazırlanıyor..." />;
  }

  return isAuthenticated ? <Navigate to="/module-selection" replace /> : children;
};

const InternalAuditRoute = ({ children, adminOnly = false }) => {
  const { loading, isAuthenticated, hasModuleAccess, isAdmin } = useAuth();

  if (loading) {
    return <Loading fullScreen={true} message="Yetkiler kontrol ediliyor..." />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (adminOnly && !isAdmin()) {
    return <Navigate to="/module-selection" replace />;
  }

  if (!hasModuleAccess('internal_audit')) {
    return <Navigate to="/module-selection" replace />;
  }

  return children;
};

const VehicleMaintenanceRoute = ({ children, adminOnly = false }) => {
  const { loading, isAuthenticated, hasModuleAccess, isAdmin } = useAuth();

  if (loading) {
    return <Loading fullScreen={true} message="Yetkiler kontrol ediliyor..." />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (adminOnly && !isAdmin()) {
    return <Navigate to="/module-selection" replace />;
  }

  if (!hasModuleAccess('vehicle_maintenance')) {
    return <Navigate to="/module-selection" replace />;
  }

  return children;
};

const CamasirhaneRoute = ({ children, adminOnly = false }) => {
  const { loading, isAuthenticated, hasModuleAccess, isAdmin } = useAuth();

  if (loading) {
    return <Loading fullScreen={true} message="Yetkiler kontrol ediliyor..." />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (adminOnly && !isAdmin()) {
    return <Navigate to="/module-selection" replace />;
  }

  if (!hasModuleAccess('camasirhane')) {
    return <Navigate to="/module-selection" replace />;
  }

  return children;
};

const InternalAuditFeatureRoute = ({ children, featureModuleCode }) => {
  const { loading, isAuthenticated, hasModuleAccess } = useAuth();

  if (loading) {
    return <Loading fullScreen={true} message="Yetkiler kontrol ediliyor..." />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!hasModuleAccess('internal_audit')) {
    return <Navigate to="/module-selection" replace />;
  }

  if (featureModuleCode && !hasModuleAccess(featureModuleCode)) {
    return <Navigate to="/internal-audit/doc-convert" replace />;
  }

  return children;
};

const QualityDofAccessAdminRoute = ({ children }) => {
  const { loading, isAuthenticated, isAdmin } = useAuth();

  if (loading) {
    return <Loading fullScreen={true} message="Yetkiler kontrol ediliyor..." />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!isAdmin()) {
    return <Navigate to="/module-selection" replace />;
  }

  return children;
};

// Ana uygulama component'i - routing ile
const AppContent = () => {
  return (
    <Routes>
      <Route path="/login" element={
        <PublicRoute>
          <LoginPage />
        </PublicRoute>
      } />
      <Route path="/forgot-password" element={
        <PublicRoute>
          <ForgotPasswordPage />
        </PublicRoute>
      } />
      <Route path="/reset-password" element={
        <PublicRoute>
          <ResetPasswordPage />
        </PublicRoute>
      } />
      <Route path="/change-password" element={
        <ProtectedRoute>
          <ChangePasswordPage />
        </ProtectedRoute>
      } />
      <Route path="/login/microsoft" element={
        <ProtectedRoute>
          <MicrosoftMailLoginPage />
        </ProtectedRoute>
      } />
      <Route path="/mailcall" element={
        <ProtectedRoute>
          <MailCallCallbackPage />
        </ProtectedRoute>
      } />
      <Route path="/mail/*" element={
        <ProtectedRoute>
          <MainLayout>
            <MailModuleLayout />
          </MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/module-selection" element={
        <ProtectedRoute>
          <ModuleSelectionPage />
        </ProtectedRoute>
      } />
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <MainLayout>
            <DashboardPage />
          </MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/board" element={
        <ProtectedRoute>
          <MainLayout>
            <BoardPage />
          </MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/tickets" element={
        <ProtectedRoute>
          <MainLayout>
            <TicketPage />
          </MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/projects" element={
        <ProtectedRoute>
          <MainLayout>
            <ProjectsPage />
          </MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/projects/create" element={
        <ProtectedRoute>
          <MainLayout>
            <CreateProjectPage />
          </MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/projects/:projectId/statuses" element={
        <ProtectedRoute>
          <MainLayout>
            <ProjectStatusManagement />
          </MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/reports" element={
        <ProtectedRoute workReportAccess>
          <MainLayout>
            <ReportsPage />
          </MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/team" element={
        <ProtectedRoute>
          <MainLayout>
            <div className="p-8 text-center">
              <h1 className="text-2xl font-bold text-gray-900 mb-4">Team</h1>
              <p className="text-gray-600">Team management page coming soon...</p>
            </div>
          </MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/changelog" element={
        <ProtectedRoute>
          <ChangelogPage />
        </ProtectedRoute>
      } />
      <Route path="/settings" element={
        <ProtectedRoute>
          <MainLayout>
            <SettingsPage />
          </MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/profile" element={
        <ProtectedRoute>
          <MainLayout>
            <ProfilePage />
          </MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/sales-call" element={
        <ProtectedRoute>
          <MainLayout>
            <SalesCallPage />
          </MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/sales-call/dashboard" element={
        <ProtectedRoute>
          <MainLayout>
            <SalesCallPage />
          </MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/sales-call/calendar" element={
        <ProtectedRoute>
          <MainLayout>
            <SalesCallPage />
          </MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/sales-call/road-planner-monthly" element={
        <ProtectedRoute>
          <MainLayout>
            <RoadPlannerMonthly />
          </MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/sales-call/visit-report" element={
        <ProtectedRoute>
          <MainLayout>
            <B2BMeetingsDashboard />
          </MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/sales-call/expenses" element={
        <ProtectedRoute>
          <MainLayout>
            <TravelExpensesPage />
          </MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/sales-call/mail-schedules" element={
        <ProtectedRoute>
          <MainLayout>
            <SalesCallMailSchedulesPage />
          </MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/sales-call/sub-agencies" element={
        <ProtectedRoute>
          <MainLayout>
            <SubAgencyPage />
          </MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/sales-call/address-book" element={
        <ProtectedRoute>
          <MainLayout>
            <AddressBookPage />
          </MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/sales-call/ai-insights" element={
        <ProtectedRoute>
          <MainLayout>
            <AIInsightsDashboard />
          </MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/sales-call/record-analyses" element={
        <ProtectedRoute>
          <MainLayout>
            <AIRecordAnalyses />
          </MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/stopsale/dashboard" element={
        <ProtectedRoute>
          <MainLayout>
            <StopSaleDashboard />
          </MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/stopsale/actions" element={
        <ProtectedRoute>
          <MainLayout>
            <StopSaleActions />
          </MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/stopsale/calendar" element={
        <ProtectedRoute>
          <MainLayout>
            <StopSaleCalendar />
          </MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/stopsale/mail-recipients" element={
        <ProtectedRoute>
          <MainLayout>
            <StopSaleMailRecipientsPage />
          </MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/marketing-budget/dashboard" element={
        <ProtectedRoute>
          <MainLayout>
            <MarketingBudgetDashboard />
          </MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/marketing-budget/budget" element={
        <ProtectedRoute>
          <MainLayout>
            <MarketingBudget />
          </MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/marketing-budget/basic-budget" element={
        <ProtectedRoute>
          <MainLayout>
            <BasicBudget />
          </MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/marketing-budget/percent-budget" element={
        <ProtectedRoute>
          <MainLayout>
            <PercentBudget />
          </MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/marketing-budget/reports" element={
        <ProtectedRoute>
          <MainLayout>
            <MarketingBudgetReports />
          </MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/marketing-budget/manager-view" element={
        <ProtectedRoute>
          <MainLayout>
            <ManagerViewPage />
          </MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/marketing-budget/test-grid" element={
        <ProtectedRoute>
          <MainLayout>
            <TestBudgetGrid />
          </MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/marketing-budget/tables" element={
        <ProtectedRoute>
          <MainLayout>
            <MarketingBudgetTables />
          </MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/definitions" element={<Navigate to="/marketing-budget/definitions" replace />} />
      <Route path="/marketing-budget/definitions" element={
        <ProtectedRoute>
          <MainLayout>
            <MarketingBudgetDefinitions />
          </MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/marketing-budget/analysis" element={
        <ProtectedRoute>
          <MainLayout>
            <MarketingBudgetAnalysis />
          </MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/marketing-budget/airport-tourist-data" element={
        <ProtectedRoute>
          <MainLayout>
            <AirportTouristData />
          </MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/content-management/*" element={
        <ProtectedRoute>
          <MainLayout>
            <ContentManagement />
          </MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/marketing-management" element={
        <ProtectedRoute>
          <MainLayout>
            <MarketingManagementPage />
          </MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/marketing-management/mailing-planner" element={
        <ProtectedRoute>
          <MainLayout>
            <MailingPlanner />
          </MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/marketing-management/mailing-calendar" element={
        <ProtectedRoute>
          <MainLayout>
            <MailingCalendar />
          </MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/marketing-management/content-planner" element={
        <ProtectedRoute>
          <MainLayout>
            <ContentPlanner />
          </MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/marketing-management/content-calendar" element={
        <ProtectedRoute>
          <MainLayout>
            <ContentCalendar />
          </MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/marketing-management/content-presentation" element={
        <ProtectedRoute>
          <MainLayout>
            <ContentPresentationTable />
          </MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/survey" element={<Navigate to="/survey/dashboard" replace />} />
      <Route path="/survey/dashboard" element={
        <ProtectedRoute>
          <MainLayout>
            <SurveyDashboard />
          </MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/survey/list" element={
        <ProtectedRoute>
          <MainLayout>
            <SurveyList />
          </MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/survey/create" element={
        <ProtectedRoute>
          <MainLayout>
            <SurveyCreate />
          </MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/survey/edit/:slug" element={
        <ProtectedRoute>
          <MainLayout>
            <SurveyCreate />
          </MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/survey/submissions/:slug" element={
        <ProtectedRoute>
          <MainLayout>
            <SurveySubmissions />
          </MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/survey/submissions/detail/:id" element={
        <ProtectedRoute>
          <MainLayout>
            <SurveySubmissionDetail />
          </MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/survey/analytics/:slug" element={
        <ProtectedRoute>
          <MainLayout>
            <SurveyAnalytics />
          </MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/survey/submissions/all" element={
        <ProtectedRoute>
          <MainLayout>
            <SurveyGlobalSubmissions />
          </MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/survey/analytics/all" element={
        <ProtectedRoute>
          <MainLayout>
            <SurveyGlobalAnalytics />
          </MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/s/:slug" element={<SurveyFill />} />
      <Route path="/doc-convert/*" element={<Navigate to="/internal-audit/doc-convert" replace />} />
      <Route path="/transcription/*" element={<Navigate to="/internal-audit/transcription" replace />} />
      <Route path="/notes" element={
        <ProtectedRoute>
          <NotesLayout />
        </ProtectedRoute>
      }>
        <Route index element={<NotesPage />} />
        <Route path=":noteId" element={<NotesPage />} />
      </Route>
      <Route path="/quality-management/dashboard" element={<Navigate to="/quality-management/df-baslat" replace />} />
      <Route path="/quality-management/forms" element={<Navigate to="/quality-management/df-baslat" replace />} />
      <Route path="/quality-management/doif-dashboard" element={
        <ProtectedRoute>
          <MainLayout>
            <DoifDashboardPage />
          </MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/quality-management/df-baslat" element={
        <ProtectedRoute>
          <MainLayout>
            <QualityManagementPage section="df-baslat" />
          </MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/quality-management/df-islemleri" element={
        <ProtectedRoute>
          <MainLayout>
            <QualityManagementPage section="df-islemleri" />
          </MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/quality-management/df-onay" element={
        <ProtectedRoute>
          <MainLayout>
            <QualityManagementPage section="df-onay" />
          </MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/quality-management/df-kapatma" element={
        <ProtectedRoute>
          <MainLayout>
            <QualityManagementPage section="df-kapatma" />
          </MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/quality-management/board" element={
        <ProtectedRoute>
          <MainLayout>
            <QualityManagementPage section="board" />
          </MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/quality-management/definitions" element={<Navigate to="/quality-management/definitions/hotels" replace />} />
      <Route path="/quality-management/departments" element={<Navigate to="/quality-management/definitions/departments" replace />} />
      <Route path="/quality-management/definitions/df-definitions" element={
        <ProtectedRoute>
          <MainLayout>
            <QualityManagementPage section="df-definitions" />
          </MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/quality-management/definitions/hotels" element={
        <ProtectedRoute>
          <MainLayout>
            <QualityManagementPage section="hotels" />
          </MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/quality-management/definitions/departments" element={
        <ProtectedRoute>
          <MainLayout>
            <QualityManagementPage section="departments" />
          </MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/quality-management/definitions/users" element={
        <ProtectedRoute>
          <MainLayout>
            <QualityManagementPage section="users" />
          </MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/quality-management/definitions/nonconformity-summaries" element={
        <ProtectedRoute>
          <MainLayout>
            <QualityManagementPage section="nonconformity-summaries" />
          </MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/quality-management/definitions/nonconformity-sources" element={
        <ProtectedRoute>
          <MainLayout>
            <QualityManagementPage section="nonconformity-sources" />
          </MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/quality-management/definitions/root-cause-summaries" element={
        <ProtectedRoute>
          <MainLayout>
            <QualityManagementPage section="root-cause-summaries" />
          </MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/quality-management/definitions/dof-access" element={
        <QualityDofAccessAdminRoute>
          <MainLayout>
            <QualityDofAccessManagementPage />
          </MainLayout>
        </QualityDofAccessAdminRoute>
      } />
      <Route path="/food-beverage" element={<Navigate to="/food-beverage/hotels" replace />} />
      <Route path="/food-beverage/hotels" element={
        <ProtectedRoute>
          <MainLayout>
            <FoodBeveragePage section="hotels" />
          </MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/food-beverage/product-groups" element={
        <ProtectedRoute>
          <MainLayout>
            <FoodBeveragePage section="product-groups" />
          </MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/food-beverage/areas" element={
        <ProtectedRoute>
          <MainLayout>
            <FoodBeveragePage section="areas" />
          </MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/food-beverage/products" element={
        <ProtectedRoute>
          <MainLayout>
            <FoodBeveragePage section="products" />
          </MainLayout>
        </ProtectedRoute>
      } />

      <Route path="/general" element={<Navigate to="/general/licenses" replace />} />
      <Route path="/general/licenses" element={
        <ProtectedRoute>
          <MainLayout>
            <LicenseDashboard />
          </MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/general/licenses/list" element={
        <ProtectedRoute>
          <MainLayout>
            <LicenseListPage />
          </MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/general/licenses/reports" element={
        <ProtectedRoute>
          <MainLayout>
            <LicenseReportsPage />
          </MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/general/licenses/definitions" element={
        <ProtectedRoute>
          <MainLayout>
            <LicenseDefinitionsPage />
          </MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/general/qr" element={
        <ProtectedRoute>
          <MainLayout>
            <QrCodePage />
          </MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/general/qr/custom-pages" element={
        <ProtectedRoute>
          <MainLayout>
            <CustomPagesPage />
          </MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/internal-audit/vehicles" element={<Navigate to="/vehicle-maintenance/vehicles" replace />} />
      <Route path="/internal-audit/inspections" element={<Navigate to="/vehicle-maintenance/inspections" replace />} />
      <Route path="/internal-audit/insurance" element={<Navigate to="/vehicle-maintenance/insurance" replace />} />
      <Route path="/internal-audit/maintenance" element={<Navigate to="/vehicle-maintenance/maintenance" replace />} />
      <Route path="/internal-audit/mileage" element={<Navigate to="/vehicle-maintenance/mileage" replace />} />
      <Route path="/internal-audit/traffic-fines" element={<Navigate to="/vehicle-maintenance/traffic-fines" replace />} />
      <Route path="/internal-audit/breakdowns" element={<Navigate to="/vehicle-maintenance/breakdowns" replace />} />
      <Route path="/internal-audit/accidents" element={<Navigate to="/vehicle-maintenance/accidents" replace />} />
      <Route path="/vehicle-maintenance/vehicles" element={
        <VehicleMaintenanceRoute>
          <MainLayout>
            <InternalAuditVehiclesPage />
          </MainLayout>
        </VehicleMaintenanceRoute>
      } />
      <Route path="/vehicle-maintenance/inspections" element={
        <VehicleMaintenanceRoute>
          <MainLayout>
            <InternalAuditInspectionsPage />
          </MainLayout>
        </VehicleMaintenanceRoute>
      } />
      <Route path="/vehicle-maintenance/insurance" element={
        <VehicleMaintenanceRoute>
          <MainLayout>
            <InternalAuditInsurancePage />
          </MainLayout>
        </VehicleMaintenanceRoute>
      } />
      <Route path="/vehicle-maintenance/maintenance" element={
        <VehicleMaintenanceRoute>
          <MainLayout>
            <InternalAuditMaintenancePage />
          </MainLayout>
        </VehicleMaintenanceRoute>
      } />
      <Route path="/vehicle-maintenance/mileage" element={
        <VehicleMaintenanceRoute>
          <MainLayout>
            <InternalAuditMileagePage />
          </MainLayout>
        </VehicleMaintenanceRoute>
      } />
      <Route path="/vehicle-maintenance/traffic-fines" element={
        <VehicleMaintenanceRoute>
          <MainLayout>
            <InternalAuditTrafficFinesPage />
          </MainLayout>
        </VehicleMaintenanceRoute>
      } />
      <Route path="/vehicle-maintenance/breakdowns" element={
        <VehicleMaintenanceRoute>
          <MainLayout>
            <InternalAuditBreakdownsPage />
          </MainLayout>
        </VehicleMaintenanceRoute>
      } />
      <Route path="/vehicle-maintenance/accidents" element={
        <VehicleMaintenanceRoute>
          <MainLayout>
            <InternalAuditAccidentsPage />
          </MainLayout>
        </VehicleMaintenanceRoute>
      } />
      <Route path="/vehicle-maintenance/access" element={
        <VehicleMaintenanceRoute adminOnly={true}>
          <MainLayout>
            <VehicleMaintenanceAccessPage />
          </MainLayout>
        </VehicleMaintenanceRoute>
      } />
      <Route path="/vehicle-maintenance/audit-logs" element={
        <VehicleMaintenanceRoute adminOnly={true}>
          <MainLayout>
            <InternalAuditVehicleAuditLogPage />
          </MainLayout>
        </VehicleMaintenanceRoute>
      } />
      <Route path="/camasirhane" element={
        <CamasirhaneRoute>
          <MainLayout>
            <CamasirhaneLayout />
          </MainLayout>
        </CamasirhaneRoute>
      }>
        <Route index element={<Navigate to="/camasirhane/dashboard" replace />} />
        <Route path="dashboard" element={<DashboardScreen />} />
        <Route path="weighing" element={<WeighingEntryScreen />} />
        <Route path="production" element={<ProductionEntryScreen />} />
        <Route path="summary" element={<SummaryTotalsScreen />} />
        <Route path="cost" element={<CostEntryScreen />} />
        <Route path="iron" element={<IronMeasurementScreen />} />
        <Route path="import" element={<ImportExportScreen />} />
        <Route path="admin" element={<AdminScreen />} />
        <Route path="definitions" element={<DefinitionsScreen />} />
        <Route path="access" element={
          <CamasirhaneRoute adminOnly={true}>
            <CamasirhaneAccessPage />
          </CamasirhaneRoute>
        } />
      </Route>
      <Route path="/internal-audit/access-management" element={
        <InternalAuditRoute adminOnly={true}>
          <MainLayout>
            <InternalAuditAccessManagementPage />
          </MainLayout>
        </InternalAuditRoute>
      } />
      <Route path="/internal-audit/doc-convert" element={
        <InternalAuditFeatureRoute featureModuleCode="doc_convert">
          <MainLayout>
            <WordToExcelPage />
          </MainLayout>
        </InternalAuditFeatureRoute>
      } />
      <Route path="/internal-audit/doc-convert/dashboard" element={
        <InternalAuditFeatureRoute featureModuleCode="doc_convert">
          <MainLayout>
            <NotesDashboardPage />
          </MainLayout>
        </InternalAuditFeatureRoute>
      } />
      <Route path="/internal-audit/doc-convert/notes" element={
        <InternalAuditFeatureRoute featureModuleCode="doc_convert">
          <MainLayout>
            <DailyNotesPage />
          </MainLayout>
        </InternalAuditFeatureRoute>
      } />
      <Route path="/internal-audit/doc-convert/folders" element={
        <InternalAuditFeatureRoute featureModuleCode="doc_convert">
          <MainLayout>
            <NoteFoldersPage />
          </MainLayout>
        </InternalAuditFeatureRoute>
      } />
      <Route path="/internal-audit/doc-convert/my-notes" element={
        <InternalAuditFeatureRoute featureModuleCode="doc_convert">
          <MainLayout>
            <MyNotesDashboardPage />
          </MainLayout>
        </InternalAuditFeatureRoute>
      } />
      <Route path="/internal-audit/doc-convert/access" element={
        <InternalAuditRoute adminOnly={true}>
          <MainLayout>
            <DocConvertAccessPage />
          </MainLayout>
        </InternalAuditRoute>
      } />
      <Route path="/internal-audit/transcription" element={
        <InternalAuditFeatureRoute featureModuleCode="transcription">
          <MainLayout>
            <TranscriptionPage />
          </MainLayout>
        </InternalAuditFeatureRoute>
      } />
      <Route path="/internal-audit/transcription/access" element={
        <InternalAuditRoute adminOnly={true}>
          <MainLayout>
            <TranscriptionAccessPage />
          </MainLayout>
        </InternalAuditRoute>
      } />
      <Route path="/internal-audit/technical-maintenance" element={
        <InternalAuditFeatureRoute featureModuleCode="technical_maintenance">
          <MainLayout>
            <TechnicalMaintenanceDashboardPage />
          </MainLayout>
        </InternalAuditFeatureRoute>
      } />
      <Route path="/internal-audit/technical-maintenance/equipment" element={
        <InternalAuditFeatureRoute featureModuleCode="technical_maintenance">
          <MainLayout>
            <TechnicalMaintenanceEquipmentPage />
          </MainLayout>
        </InternalAuditFeatureRoute>
      } />
      <Route path="/internal-audit/technical-maintenance/equipment/:id" element={
        <InternalAuditFeatureRoute featureModuleCode="technical_maintenance">
          <MainLayout>
            <TechnicalMaintenanceEquipmentDetailPage />
          </MainLayout>
        </InternalAuditFeatureRoute>
      } />
      <Route path="/internal-audit/technical-maintenance/schedule" element={
        <InternalAuditFeatureRoute featureModuleCode="technical_maintenance">
          <MainLayout>
            <TechnicalMaintenanceSchedulePage />
          </MainLayout>
        </InternalAuditFeatureRoute>
      } />
      <Route path="/internal-audit/technical-maintenance/maintenance" element={
        <InternalAuditFeatureRoute featureModuleCode="technical_maintenance">
          <MainLayout>
            <TechnicalMaintenanceRecordsPage />
          </MainLayout>
        </InternalAuditFeatureRoute>
      } />
      <Route path="/internal-audit/technical-maintenance/reports" element={
        <InternalAuditFeatureRoute featureModuleCode="technical_maintenance">
          <MainLayout>
            <TechnicalMaintenanceReportsPage />
          </MainLayout>
        </InternalAuditFeatureRoute>
      } />
      <Route path="/internal-audit/technical-maintenance/invoices" element={
        <InternalAuditFeatureRoute featureModuleCode="technical_maintenance">
          <MainLayout>
            <TechnicalMaintenanceInvoicesPage />
          </MainLayout>
        </InternalAuditFeatureRoute>
      } />
      <Route path="/internal-audit/technical-maintenance/activity" element={
        <InternalAuditFeatureRoute featureModuleCode="technical_maintenance">
          <MainLayout>
            <TechnicalMaintenanceActivityPage />
          </MainLayout>
        </InternalAuditFeatureRoute>
      } />
      <Route path="/internal-audit/technical-maintenance/access" element={
        <InternalAuditRoute adminOnly={true}>
          <MainLayout>
            <TechnicalMaintenanceAccessPage />
          </MainLayout>
        </InternalAuditRoute>
      } />

      <Route path="/helpdesk/dashboard" element={
        <ProtectedRoute>
          <MainLayout>
            <DashboardPage />
          </MainLayout>
        </ProtectedRoute>
      } />
      <Route path="/" element={<Navigate to="/login" replace />} />
    </Routes>
  );
};

// Ana App component'i - AuthProvider ve Router ile sarılı
function App() {
  return (
    <AuthProvider>
      <LanguageProvider>
        <FontProvider>
          <ThemeProvider>
            <ProjectProvider>
              <NotificationProvider>
                <Router
                  future={{
                    v7_startTransition: true,
                    v7_relativeSplatPath: true,
                  }}
                >
                  <TicketProvider>
                    <ErrorBoundary>
                      <div className="App">
                        <AppContent />
                      </div>
                    </ErrorBoundary>
                  </TicketProvider>
                </Router>
              </NotificationProvider>
            </ProjectProvider>
          </ThemeProvider>
        </FontProvider>
      </LanguageProvider>
    </AuthProvider>
  );
}

export default App;